import { Typography } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { BlockedScoreTooltip } from 'src/components/BlockedScoreTooltip';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import Score, { SCORE_SIZE } from 'src/components/Score';
import { BLOCKED_REASON } from 'src/data/common';
import { getToolDescription, getToolTitleForTooltip } from 'src/data/generationTool';
import { SCORING_METHOD_TYPE } from 'src/data/scoring';

import { useStyles } from './useStyles';

export const ScoreWithDetailsLink = ({
  className,
  score,
  expanded,
  locked,
  loading,
  onClick,
  blockedReason,
  displayInsights,
  generationTool,
  scoringMethod,
  supportSize,
  mixtureSubAssetType,
  isNonEnglishLanguage,
  tooltipOverride,
  ...restProps
}) => {
  const styles = useStyles();
  const generationToolIcon = generationTool && (
    <GenerationToolIcon
      className={styles.icon}
      tool={generationTool}
    />
  );
  const makeTooltipTextMessage = (scoringMethod) => {
    return scoringMethod?.type === SCORING_METHOD_TYPE.customModel
      ? `\n\nThis score predicts the performance of this text compared to ${supportSize} similar texts from Anyword’s dataset and your connected datasets. The score predicts either click-through or conversion rate, normalized to be between 0 and 100.`
      : `\n\nThis score predicts the performance of this text compared to ${supportSize} similar texts from Anyword’s dataset. The score predicts either click-through or conversion rate, normalized to be between 0 and 100. Some product or service copy categories are more engaging than others and will likely start at higher baseline scores.`;
  };

  const title = getToolTitleForTooltip(generationTool);
  const description = mixtureSubAssetType
    ? mixtureSubAssetType
    : getToolDescription(generationTool);
  const tooltipTitle = tooltipOverride
    ? tooltipOverride.title
    : `${title}${description && ' ' + description} Score`;
  const tooltipText = tooltipOverride
    ? tooltipOverride.text
    : makeTooltipTextMessage(scoringMethod);
  const message = tooltipTitle + tooltipText;
  return (
    <BlockedScoreTooltip
      blockedReason={blockedReason}
      nonBlockedTitle={isNonEnglishLanguage ? null : message}
      placement={'bottom'}
    >
      <div
        className={cx(styles.root, className, {
          [styles.expanded]: expanded,
          [styles.locked]: locked,
        })}
        onClick={onClick}
        data-testid="scoreWithDetailsLink"
      >
        <Score
          className={styles.score}
          score={score}
          locked={locked}
          blockedReason={blockedReason}
          loading={loading}
          size={SCORE_SIZE.large}
          generationToolIcon={generationToolIcon}
          {...restProps}
        />
        {displayInsights && (
          <Typography className={styles.text}>
            Insights <span className={styles.arrow}>&rsaquo;</span>
          </Typography>
        )}
      </div>
    </BlockedScoreTooltip>
  );
};

ScoreWithDetailsLink.propTypes = {
  className: PropTypes.string,
  score: PropTypes.number,
  expanded: PropTypes.bool,
  locked: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  blockedReason: PropTypes.oneOf(Object.values(BLOCKED_REASON)),
  displayInsights: PropTypes.bool,
  generationTool: PropTypes.string,
  supportSize: PropTypes.number,
  scoringMethod: PropTypes.shape({ type: PropTypes.string }),
  mixtureSubAssetType: PropTypes.string,
  isNonEnglishLanguage: PropTypes.bool,
  tooltipOverride: PropTypes.shape({ title: PropTypes.string, text: PropTypes.string }),
};
ScoreWithDetailsLink.defaultProps = {
  className: null,
  score: null,
  expanded: false,
  locked: false,
  loading: false,
  onClick: null,
  blockedReason: null,
  displayInsights: true,
  generationTool: null,
  supportSize: null,
  scoringMethod: null,
  mixtureSubAssetType: null,
  isNonEnglishLanguage: false,
  tooltipOverride: null,
};
